/**
 * Fait : encadre UNE partie d'écran — son étiquette, son titre, et ce qu'elle
 *   contient. C'est le gabarit qui donne à voir la structure d'une page au
 *   lieu de la laisser deviner : chaque partie est un bloc détaché, avec son
 *   fond, son filet et son nom en capitales.
 *
 * Ne fait pas : il ne décide d'aucun contenu, ne porte aucune règle de droit,
 *   et n'impose pas ce qu'il y a dedans — une liste, un formulaire, un
 *   paragraphe. Il ne fait que la SÉPARATION.
 *
 *   Pourquoi il existe, écrit le 2026-08-03 : Charles a ouvert l'atelier pour
 *   la première fois et n'a rien pu suivre — cinq blocs empilés sans une
 *   marque de séparation, et deux boutons qui semblaient appartenir au bloc
 *   d'au-dessus. Ses mots : « les divisions et les choses claires, c'est très
 *   important pour un malvoyant ».
 */

import type { ReactNode, Ref } from 'react';

/**
 * La NATURE d'une partie, qui décide de la couleur de son bandeau.
 *
 * Quatre valeurs et pas une de plus : au-delà, les couleurs cessent d'être
 * distinguables les unes des autres et le repère se dissout. C'est une
 * classification du contenu — de quoi parle cette partie — jamais un choix
 * de teinte laissé à l'écran appelant.
 */
export type NatureDePartie = 'projet' | 'travail' | 'gens' | 'partage';

export function Partie({
  etiquette,
  titre,
  nature = 'projet',
  niveauDeTitre = 3,
  aide,
  enTete,
  refDuTitre,
  children,
  ...reste
}: {
  /** Le nom de la partie, écrit en blanc dans le bandeau de couleur. */
  readonly etiquette: string;

  /** Le titre lisible de la partie. */
  readonly titre: string;

  /** Ce dont la partie parle — c'est ce qui donne sa couleur au bandeau. */
  readonly nature?: NatureDePartie;

  /** 2 pour une partie principale d'écran, 3 pour une partie interne. */
  readonly niveauDeTitre?: 2 | 3;

  /** Une phrase qui dit à quoi sert la partie, quand ce n'est pas évident. */
  readonly aide?: string;

  /** Ce qui se pose sous le titre — un compte, un état. */
  readonly enTete?: ReactNode;

  /**
   * Pour déplacer le focus sur ce titre à l'arrivée sur un écran. Le titre
   * devient alors focalisable par le code (`tabIndex={-1}`) sans entrer dans
   * le parcours du clavier — on n'a pas à tabuler dessus pour l'atteindre.
   */
  readonly refDuTitre?: Ref<HTMLHeadingElement>;

  readonly children: ReactNode;
} & Record<`data-${string}`, string | undefined>) {
  const Titre = niveauDeTitre === 2 ? 'h2' : 'h3';

  return (
    <section className="partie" data-nature={nature} {...reste}>
      <header>
        <span className="etiquette-de-partie">{etiquette}</span>
      </header>

      <div className="contenu-de-partie">
        <Titre {...(refDuTitre === undefined ? {} : { ref: refDuTitre, tabIndex: -1 })}>
          {titre}
        </Titre>
        {aide !== undefined && <p className="texte-attenue">{aide}</p>}
        {enTete}
        {children}
      </div>
    </section>
  );
}
